import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ThousandSuffixesPipe } from 'src/app/pipe/thousand.pipe';
import { TemporalityWidths } from '../../chartmap-mosaic-shared';

const DAY_MS = 86_400_000;

// One row of the score/sentiment events mosaic: magnitude bar + delta pill
// (+ sparkline/temporality when sole). Presentational; callers normalise the
// value/prev/format for both the score and sentiment shapes.
@Component({
  selector: 'app-event-row',
  templateUrl: './event-row.component.html',
  styleUrls: ['./event-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventRowComponent implements OnChanges {
  @Input() name = '';
  @Input() value = 0;
  @Input() prevValue: number | null | undefined = null;
  @Input() impact = 1; // +1 good, -1 bad
  @Input() valueFormat: 'count' | 'percent' = 'count';
  @Input() maxValue = 0;

  @Input() series: [number, number][] | undefined;
  @Input() microTimeframe = '';
  /** The user's global timeframe, in days — decides whether a peak counts as outside it. */
  @Input() bandDays?: number;
  /** Sparkline bucket size in days (7 = weekly bars across the score window). */
  @Input() bucketDays?: number;
  @Input() showSparkline = false;
  @Input() showTemporality = false;
  @Input() temporalityWidths: TemporalityWidths | null = null;

  @Input() @HostBinding('class.sole') sole = false;
  @Input() @HostBinding('class.mobile') mobile = false;
  @Input() @HostBinding('class.active') active = false;
  @Input() @HostBinding('id') elementId: string | null = null;

  @Output() rowClick = new EventEmitter<void>();

  /**
   * When this event's busiest day falls outside the selected timeframe — the
   * case where a large contribution has no visible articles behind it. Null
   * otherwise, so the note only appears where it explains something.
   */
  peakNote: string | null = null;

  @HostListener('click')
  onClick(): void {
    this.rowClick.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['series'] ||
      changes['bandDays'] ||
      changes['bucketDays'] ||
      changes['showSparkline']
    ) {
      this.peakNote = this.computePeakNote();
    }
  }

  // Picks the busiest bucket first so the note always names the tallest bar the
  // sparkline draws, then the busiest day inside it so the age stays precise.
  private computePeakNote(): string | null {
    if (!this.showSparkline || !this.bandDays || !this.series?.length) {
      return null;
    }
    const size = Math.max(1, this.bucketDays ?? 1);
    const buckets = new Map<
      number,
      { total: number; peakAge: number; peak: number }
    >();

    for (const [timestamp, count] of this.series) {
      if (count <= 0) continue;
      const age = Math.floor((Date.now() - timestamp) / DAY_MS);
      const index = Math.max(0, Math.floor(age / size));
      const bucket = buckets.get(index);
      if (!bucket) {
        buckets.set(index, { total: count, peakAge: age, peak: count });
      } else {
        bucket.total += count;
        if (count > bucket.peak) {
          bucket.peak = count;
          bucket.peakAge = age;
        }
      }
    }
    if (!buckets.size) {
      return null;
    }

    let best = { total: 0, peakAge: 0, peak: 0 };
    for (const bucket of buckets.values()) {
      if (bucket.total > best.total) best = bucket;
    }
    if (best.peakAge <= this.bandDays) {
      return null;
    }
    return best.peakAge < 14
      ? `peak ${best.peakAge}d ago`
      : `peak ${Math.round(best.peakAge / 7)}w ago`;
  }

  private get hasPrev(): boolean {
    return this.prevValue !== null && this.prevValue !== undefined;
  }

  get barWidth(): number {
    if (!this.maxValue || this.maxValue <= 0) return 0;
    return Math.min(100, (this.value / this.maxValue) * 100);
  }

  get direction(): 'up' | 'down' | 'flat' {
    if (!this.hasPrev) return 'flat';
    const prev = this.prevValue as number;
    if (this.value > prev) return 'up';
    if (this.value < prev) return 'down';
    return 'flat';
  }

  get deltaArrow(): string {
    if (this.direction === 'up') return '↑';
    if (this.direction === 'down') return '↓';
    return '';
  }

  // Polarity-aware: good when the move helps (rise in +1, or fall in -1).
  get deltaClass(): 'positive' | 'negative' | 'neutral' {
    if (this.direction === 'flat') return 'neutral';
    const imp = this.impact ?? 1;
    const good =
      (imp === 1 && this.direction === 'up') ||
      (imp === -1 && this.direction === 'down');
    return good ? 'positive' : 'negative';
  }

  private thousand = new ThousandSuffixesPipe();

  // Absolute change (a relative ratio explodes for tiny baselines): count delta,
  // or percentage-point delta shown as %. `NEW` when there's no prior point.
  get deltaLabel(): string {
    if (!this.hasPrev) return this.value > 0 ? 'NEW' : '';
    const diff = Math.abs(this.value - (this.prevValue as number));
    if (this.valueFormat === 'percent') {
      const points = diff * 100;
      if (points === 0) return '0%';
      return (
        (points < 1 ? points.toFixed(1) : String(Math.round(points))) + '%'
      );
    }
    return String(this.thousand.transform(diff));
  }
}
